import React, { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import ExplodeNestedModal, { missionHasExplodableArtifacts } from '../components/ExplodeNestedModal';
import AddMilestoneChoiceModal from '../components/AddMilestoneChoiceModal';
import { useDragMenuState } from './useDragMenuState';
import { useHandleClick } from './useHandleClick';
import { MilestoneMenu, NoteMenu } from './MarkerMenus';
import { MissionMenu } from './MissionMenu';
import './DragHandleMenu.css';

/**
 * Notion-style drag handle. A click opens this menu; a drag moves the node.
 * Right-click and other callers open it through openAtPosition.
 */
const DragHandleMenu = forwardRef(({
  task,
  onDelete,
  onDuplicate,
  onCreateParallelMission,
  onCopy,
  onFocusTask,
  hasNestedSprint,
  onOpenNestedSprint,
  onCreateNestedSprint,
  hasIcon,
  onAddIcon,
  onOpenDetails,
  onOpenAttachments,
  onArchive,
  onUnarchive,
  onExplodeNestedCampaign,
  nestedSprint = null,
  onEditMilestone,
  onAddMilestoneOnHost,
  onAddNoteOnHost,
}, ref) => {
  const isMilestone = task?.nodeType === 'milestone';
  const isNote = task?.nodeType === 'note';
  const menu = useDragMenuState(ref, { task, onAddIcon });
  const { handleMouseDown } = useHandleClick({
    handleRef: menu.handleRef,
    setShowMenu: menu.setShowMenu,
    setMenuPosition: menu.setMenuPosition,
  });

  const menuContent = menu.showMenu && (isNote ? (
    <NoteMenu
      menuRef={menu.menuRef}
      menuPosition={menu.menuPosition}
      handleAction={menu.handleAction}
      onCopy={onCopy}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
    />
  ) : isMilestone ? (
    <MilestoneMenu
      menuRef={menu.menuRef}
      menuPosition={menu.menuPosition}
      handleAction={menu.handleAction}
      onEditMilestone={onEditMilestone}
      onCopy={onCopy}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
    />
  ) : (
    <MissionMenu
      menuRef={menu.menuRef}
      menuPosition={menu.menuPosition}
      task={task}
      hasIcon={hasIcon}
      hasNestedSprint={hasNestedSprint}
      onOpenDetails={onOpenDetails}
      onAddNoteOnHost={onAddNoteOnHost}
      onAddMilestoneOnHost={onAddMilestoneOnHost ? () => menu.setMilestoneChoiceOpen(true) : null}
      onOpenAttachments={onOpenAttachments}
      onFocusTask={onFocusTask}
      onAddIcon={menu.handleAddIcon}
      onCopy={onCopy}
      onDuplicate={onDuplicate}
      onCreateParallelMission={onCreateParallelMission}
      onOpenNestedSprint={onOpenNestedSprint}
      onCreateNestedSprint={onCreateNestedSprint}
      onExplode={hasNestedSprint && onExplodeNestedCampaign ? (e) => {
        e.stopPropagation();
        menu.setShowMenu(false);
        menu.setExplodeModalOpen(true);
      } : null}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onDelete={onDelete}
      handleAction={menu.handleAction}
    />
  ));

  return (
    <>
      <div
        className="task-node__drag-zone"
        onMouseDown={handleMouseDown}
        title="Drag to move"
      />
      <div
        ref={menu.handleRef}
        className={`task-node__drag-handle ${menu.showMenu ? 'task-node__drag-handle--active' : ''}`}
        onMouseDown={handleMouseDown}
        title="Click for options, drag to move"
      />
      {menu.showMenu && createPortal(menuContent, document.body)}
      <ExplodeNestedModal
        isOpen={menu.explodeModalOpen}
        missionName={task?.name}
        innerMissionCount={nestedSprint?.tasks?.length ?? 0}
        willLoseArtifacts={missionHasExplodableArtifacts(task)}
        onCancel={() => menu.setExplodeModalOpen(false)}
        onConfirm={() => {
          menu.setExplodeModalOpen(false);
          onExplodeNestedCampaign?.(task.id);
        }}
      />
      <AddMilestoneChoiceModal
        isOpen={menu.milestoneChoiceOpen}
        missionName={task?.name}
        onCancel={() => menu.setMilestoneChoiceOpen(false)}
        onChoose={(side) => {
          menu.setMilestoneChoiceOpen(false);
          onAddMilestoneOnHost?.(task.id, side);
        }}
      />
    </>
  );
});

DragHandleMenu.displayName = 'DragHandleMenu';

export default DragHandleMenu;
